//@ts-expect-error: No ts package for "@joplin/turnDown-plugin-gfm"
import { gfm } from "@joplin/turndown-plugin-gfm";
import TurndownService, { Node } from "turndown";
import { ATTACHMENT_LINK_PREFIX } from "views/Findings/constants/popups";

import { CODE_BLOCK_REGEX, escapeHtmlEntities } from "./parserUtil";

const turndownService = new TurndownService({
  blankReplacement: function (content: string, node: Node) {
    const element = node as HTMLElement;
    if (element.nodeName === "FILE-ATTACHMENT") {
      // this custom tag is considered to be blank, hence we cannot specify standard rules
      const fileName = element.getAttribute("name") || "unknown-file";
      const fileUrl = element.getAttribute("src") || "#";
      return `[${fileName}](${ATTACHMENT_LINK_PREFIX}${fileUrl})`;
    }
    return element.isContentEditable ? "\n\n" : "";
  },
  headingStyle: "atx", // to show "#" as heading tags
});

turndownService.use(gfm);

turndownService.addRule("text-color", {
  filter: function (node) {
    // Target only spans with inline style text color
    return node.nodeName === "SPAN" && !!node.style.color;
  },
  replacement: function (content, node) {
    // using wiki markdown color syntax, we can't make this work with all the interation supported platform
    const color = (node as HTMLElement).style.color;
    if (color) {
      return "{color:" + color + "}" + content + "{color}";
    }
    return content;
  },
});

turndownService.addRule("text-highlight", {
  filter: function (node) {
    // Target only mark with inline style background color
    return node.nodeName === "MARK" && !!node.style.backgroundColor;
  },
  replacement: function (content, node) {
    // This is a custom syntax based on text color above
    const color = (node as HTMLElement).style.backgroundColor;
    if (color) {
      return "{mark:" + color + "}" + content + "{mark}";
    }
    return content;
  },
});

// adding specific customize rules
turndownService.addRule("em", {
  filter: ["em"],
  replacement: function (content: string): string {
    return `*${content}*`;
  },
});

// adding specific customize rules
turndownService.addRule("strikethrough", {
  filter: ["s"],
  replacement: function (content: string): string {
    return `~~${content}~~`;
  },
});

// adding specific customize rules
turndownService.addRule("underline", {
  filter: ["u"],
  replacement: function (content: string): string {
    return `<u>${content}</u>`;
  },
});

// adding specific attachment  rules
// Attachment markdown syntax
// "[name](!attachment!/url/tto/file)"
turndownService.addRule("attachment", {
  filter: (node: HTMLElement) => {
    return node.tagName === "DIV" && node.classList.contains("ql-attachment");
  },
  replacement: function (_content: string, node: Node): string {
    const element = node as HTMLElement;
    const name = element.getAttribute("data-name") || "unknown";
    const url = element.getAttribute("data-url") || "#";
    return `[${name}](${ATTACHMENT_LINK_PREFIX}${url})`;
  },
});

// adding specific code-block rules
turndownService.addRule("code-block", {
  filter: (node: HTMLElement) => {
    // Match PRE tags (code blocks), but exclude those with ql-syntax class (handled separately if needed)
    // This catches both <pre> and <pre><code>...</code></pre> structures
    return node.tagName === "PRE";
  },
  replacement: function (_content: string, node: Node): string {
    const element = node as HTMLElement;
    // textContent decodes HTML entities, giving us the raw text for the code block
    // The escapeCodeBlocksBeforeParsing function ensures HTML is preserved as entities
    const code =
      element.querySelector("code")?.textContent || element.textContent || "";
    // Double newline before code block ensures proper separation from preceding elements (lists, quotes, etc.)
    // Double newline after ensures next element renders correctly
    return `\n\n\`\`\`\n${code.trim()}\n\`\`\`\n\n`;
  },
});

/**
 * Escapes HTML entities inside code blocks before DOM parsing.
 * This prevents the DOM parser from interpreting HTML tags inside
 * code blocks as actual HTML structure.
 *
 * Uses shared CODE_BLOCK_REGEX and escapeHtmlEntities from parserUtil.
 */
function escapeCodeBlocksBeforeParsing(html: string): string {
  if (!html) return html;

  // Reset regex lastIndex since it's a global regex
  CODE_BLOCK_REGEX.lastIndex = 0;

  return html.replace(
    CODE_BLOCK_REGEX,
    (_match, preAttrs, codeAttrs, content) => {
      const escaped = escapeHtmlEntities(content);
      return `<pre${preAttrs}><code${codeAttrs}>${escaped}</code></pre>`;
    },
  );
}

/**
 * Convert HTML to MarkDown
 * @param  {String} html The template HTML
 * @return {Text} The template Markdown
 */

export default function htmlToMarkDown(html: string): string {
  // Escape code blocks before DOM parsing to preserve HTML as text
  const escapedHtml = escapeCodeBlocksBeforeParsing(html);
  return turndownService.turndown(modifyBeforeTurndown(escapedHtml));
}

const modifyBeforeTurndown = (html: string) => {
  let modifiedString = html;

  modifiedString = checkForThTags(modifiedString);
  modifiedString = checkForImageDimensions(modifiedString);

  return modifiedString;
};

const checkForThTags = (html: string): string => {
  // Create a DOM parser to work with the HTML
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");

  // Find all tables in the HTML
  const tables = doc.querySelectorAll("table");

  tables.forEach((table) => {
    const tbody = table.querySelector("tbody");
    if (!tbody) return;

    // Find all rows in tbody that contain th tags
    const rowsWithTh = Array.from(tbody.querySelectorAll("tr")).filter((row) =>
      row.querySelector("th"),
    );

    if (rowsWithTh.length === 0) return;

    // Get or create thead
    let thead = table.querySelector("thead");
    if (!thead) {
      thead = doc.createElement("thead");
      // Insert thead before tbody
      table.insertBefore(thead, tbody);
    }

    // Move rows with th tags from tbody to thead
    rowsWithTh.forEach((row) => {
      tbody.removeChild(row);
      thead?.appendChild(row);
    });
  });

  // Return the modified HTML
  return doc.documentElement.outerHTML;
};

const checkForImageDimensions = (html: string) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");

  // Find all images in the HTML
  const imgs = doc.querySelectorAll("img");

  imgs.forEach((img) => {
    const width = img.getAttribute("width");
    const height = img.getAttribute("height");
    const originalSrc = img.getAttribute("src");

    if (originalSrc) {
      const url = new URL(originalSrc, window.location.origin);
      if (width) {
        url.searchParams.delete("width");
        url.searchParams.append("width", width);
      }
      if (height) {
        url.searchParams.delete("height");
        url.searchParams.append("height", height);
      }
      img.setAttribute("src", url.pathname + url.search);
    }
  });

  // Return the modified HTML
  return doc.documentElement.outerHTML;
};

/**
 * Escapes HTML in markdown while preserving safe formatting tags.
 * Safe tags like <u>, </u>, <s>, </s> are preserved for formatting.
 */
export const sanitizeMarkdown = (markdown: string): string => {
  // RegX for Code & Code Block
  const CODE_BLOCK_REGX =
    /(```[\s\S]*?```|`[^`]+`|(?:^(?: {4}|\t).*(?:\n|$))+)/gm;

  // Safe HTML tags that should be preserved (used for formatting)
  // Matches <u>, </u>, <s>, </s>, <sub>, </sub>, <sup>, </sup>
  const SAFE_TAGS_PATTERN = /<(\/?)(u|s|sub|sup)>/gi;

  const escapeHtmlPreservingSafeTags = (text: string): string => {
    // Temporarily replace safe tags with placeholders
    const safeTags: string[] = [];
    const textWithPlaceholders = text.replace(SAFE_TAGS_PATTERN, (match) => {
      safeTags.push(match);
      return `__SAFE_TAG_${safeTags.length - 1}__`;
    });

    // Escape remaining < and >
    const escaped = textWithPlaceholders
      .replaceAll("<", "&lt;")
      .replace(/>/g, preserveBlockQuotes);

    // Restore safe tags
    return escaped.replace(
      /__SAFE_TAG_(\d+)__/g,
      (_, index) => safeTags[index],
    );
  };

  const codeBlockMatches = markdown.match(CODE_BLOCK_REGX);
  if (codeBlockMatches?.length) {
    const splittedMarkDown = markdown.split(CODE_BLOCK_REGX);
    markdown = splittedMarkDown
      .map((markdownChunks) => {
        return codeBlockMatches.includes(markdownChunks)
          ? markdownChunks
          : escapeHtmlPreservingSafeTags(markdownChunks);
      })
      .join("")
      .replace(/^( *)&gt;/gm, ">");
  } else {
    markdown = escapeHtmlPreservingSafeTags(markdown);
  }
  return markdown;
};

function preserveBlockQuotes(match: string, offset: number, string: string) {
  // If '>' is at the start of the string or right after a newline, leave it
  if (offset === 0 || string[offset - 1] === "\n") {
    return ">";
  }
  return "&gt;";
}
